/**
 * The BA Law export, as the UI needs to reason about it.
 *
 * Kept out of the component so it can be tested without a DOM — the same split as
 * `flightPatch.ts`. The API owns the arithmetic and the policy
 * (`services/ba-law/policy.ts`); this is only how the questions that are still
 * settable are put to a human and turned back into a link.
 */

export interface ExportIssue {
  code: string;
  severity: 'blocks' | 'warns';
  row?: number;
  date?: string;
  route?: string;
  message: string;
}

export interface ExportTotals {
  legs: number;
  /** Flight time over the legs that have one. */
  hours: number;
  deductibleHours: number;
  /** Legs with no flight time entered, outside both sums. */
  legsWithoutFlightTime: number;
}

export interface ExportPolicy {
  taxYear: number;
  periodStart: string;
  periodEnd: string;
  scope: Options['scope'];
  airportCode: Options['airports'];
}

export interface CheckResponse {
  policy: ExportPolicy;
  totals: ExportTotals;
  issues: ExportIssue[];
  manifest: Array<{ initials: string; fullName: string; professional: boolean }>;
}

export interface Options {
  scope: 'mine' | 'aircraft';
  airports: 'icao' | 'iata';
}

/**
 * What the API answers with when nothing is asked of it. Mirrors
 * `DEFAULT_BA_LAW_POLICY`; the server is still the authority, and the first
 * `check` response replaces these.
 */
export const DEFAULT_OPTIONS: Options = { scope: 'mine', airports: 'icao' };

export interface Question {
  key: keyof Options;
  question: string;
  choices: Array<{ value: string; label: string; hint: string }>;
}

/**
 * The questions still put to a human, in BA Law's terms. How a jointly flown leg
 * is shown is no longer one of them: it is one row at the leg's flight time,
 * always — the logbook "share" existed only to split a turn of the Hobbs meter,
 * and went with it.
 */
export const QUESTIONS: Question[] = [
  {
    key: 'scope',
    question: 'Does the log cover the whole aircraft, or only your flying?',
    choices: [
      { value: 'mine', label: 'Only my flying', hint: 'The co-owner stays off the sheet.' },
      {
        value: 'aircraft',
        label: 'The whole aircraft',
        hint: "Adds the co-owner's legs with date, route and flight time only — nothing records their type, headcount or passengers.",
      },
    ],
  },
  {
    key: 'airports',
    question: 'Which airport codes?',
    choices: [
      { value: 'icao', label: 'ICAO, as stored', hint: 'KDPA. Their sheet does not constrain the field.' },
      {
        value: 'iata',
        label: 'Three letters',
        hint: 'DPA. Their guide asks for IATA; 83D has no three-letter form and stays as it is.',
      },
    ],
  },
];

/** The download link's query string — the same names `routes/export.ts` reads. */
export function exportQuery(options: Options): string {
  return `?scope=${options.scope}&airports=${options.airports}`;
}

/** The server's answers, as the controls in this card. */
export function optionsFromPolicy(policy: ExportPolicy): Options {
  return { scope: policy.scope, airports: policy.airportCode };
}

/**
 * Issues worst-first, and within a severity in sheet order, so the row that
 * cannot go on the sheet is the first thing read.
 */
export function orderIssues(issues: readonly ExportIssue[]): ExportIssue[] {
  const rank = (i: ExportIssue) => (i.severity === 'blocks' ? 0 : 1);
  return [...issues].sort((a, b) => rank(a) - rank(b) || (a.row ?? Infinity) - (b.row ?? Infinity));
}

export const countBlocking = (issues: readonly ExportIssue[]): number =>
  issues.filter((i) => i.severity === 'blocks').length;

/** One line of what is about to be downloaded. */
export function exportSummary(check: CheckResponse): string {
  const people = check.manifest.length;
  const missing = check.totals.legsWithoutFlightTime ?? 0;
  return (
    `${check.totals.legs} leg${check.totals.legs === 1 ? '' : 's'} · ` +
    `${check.totals.hours.toFixed(2)} h flight time · ` +
    `${check.totals.deductibleHours.toFixed(2)} h coded B, T or M · ` +
    (missing > 0 ? `${missing} with no flight time · ` : '') +
    `${people} ${people === 1 ? 'person' : 'people'} on the manifest`
  );
}
